import type { User } from "src/api/types/user";
import axios from "axios";
import { appConfig } from '../../config/app.config';


export const API_URL = appConfig.BASE_URL + appConfig.API_VERSION;


type SignInRequest = {
  email: string;
 password: string;
};

type AuthReponse = {
  status: boolean;
  code: number;
  message: string;
  data?: any;
};

type SignUpRequest = {
  email: string;
  name: string;
  password: string;
};

type SignUpResponse = {
  status: boolean;
  code: number;
  message: string;
  data?: any;
  token?: {
    access: { token: string; expires: string };
    refresh: { token: string; expires: string };
  };
};

class AuthApi {
  private ACCESS_TOKEN_KEY = "access_token";
  private CURRENT_USER_KEY = "current_user";

  async signIn(request: SignInRequest): Promise<AuthReponse & { user?: User }> {
    try {
      const response = await axios.post<
        AuthReponse & {
          access_token?: string;
          refresh_token?: string;
        }
      >(`${API_URL}/auth/login`, request, {
        withCredentials: true,
      });

      if (response.data.status) {
        // Save the tokens from the response
        if (response.data.access_token) {
          localStorage.setItem(
            this.ACCESS_TOKEN_KEY,
            response.data.access_token
          );
        }

        if (response.data.refresh_token) {
          localStorage.setItem("refresh_token", response.data.refresh_token);
        }

        const apiUser = response.data.data || {};
        const user = {
          id: apiUser.admin_id || request.email,
          email: apiUser.email || request.email,
          name: apiUser.name,
          role: apiUser.role,
          role_id: apiUser.role_id,
          status: apiUser.status,
          ...apiUser,
        } as User;

        localStorage.setItem(this.CURRENT_USER_KEY, JSON.stringify(user));
        return { ...response.data, user };
      }

      return response.data;
    } catch (error: any) {
      console.error("Login Failed: ", error);
      throw error.response?.data || { message: "Invalid email or password" };
    }
  }

  // Return the stored access token
  getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  // Clear tokens from localStorage
  private clearToken(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem("refresh_token");
    localStorage.removeItem(this.CURRENT_USER_KEY);
  }

  // Update logout method to clear local storage
  async logout(): Promise<void> {
    try {
        this.clearToken();
      await axios.post(
        `${API_URL}/auth/logout`,
        {},
        {
          withCredentials: true,
        }
      );
     // Clear the token from localStorage
    } catch (error) {
      console.error("Logout failed", error);
    }
  }

  async signUp(request: SignUpRequest): Promise<SignUpResponse> {
    try {
      const response = await axios.post<AuthReponse>(
        `${API_URL}/auth/signup`,
        request,
        {
          withCredentials: true,
        }
      );

      // if(response.data.status && response.data.tokens){
      //   this.storeTokens(response.data.tokens)
      // }

      return response.data;
    } catch (error: any) {
      console.error("Registration failed: ", error);
      throw (
        error.response?.data || {
          message: "Registration failed. Please try again.",
        }
      );
    }
  }

  async refreshToken(): Promise<void> {
    try {
      await axios.post(
        `${API_URL}/refreshTokens`,
        {},
        {
          withCredentials: true,
        }
      );
    } catch (error) {
      console.error("Token refresh failed", error);
    }
  }

  async changePassword(
    oldPassword: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<void> {
    try {
      await axios.post(
        `${API_URL}/changePassword`,
        {
          old_password: oldPassword,
          password: newPassword,
          confirm_password: confirmPassword,
        },
        { withCredentials: true }
      );
    } catch (error) {
      console.error("Password change failed:", error);
      throw error.response?.data || { message: "Failed to change password" };
    }
  }

  async me(): Promise<User> {
    try {
      // Check if we have a token
      const token = this.getAccessToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      // Check if we have cached user data
      const userStr = localStorage.getItem(this.CURRENT_USER_KEY);
      if (userStr) {
        return JSON.parse(userStr);
      }

      // If we have a token but no user data, try to fetch user data
      const response = await axios.get(`${API_URL}/admin/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      });

      if (response.data.status && response.data.data) {
        const user = response.data.data as User;
        localStorage.setItem(this.CURRENT_USER_KEY, JSON.stringify(user));
        return user;
      }

      throw new Error("Failed to get user data");
    } catch (error) {
      // Clear any stale data
      this.clearToken();
      throw new Error("Not authenticated");
    }
  }

  async forgotPassword(email: string): Promise<AuthReponse> {
    const response = await axios.post<AuthReponse>(`${API_URL}/auth/forgot-password`, { email });
    return response.data;
  }

  async resetPassword(data: { email: string; password: string; token: string }): Promise<AuthReponse> {
    const response = await axios.post<AuthReponse>(`${API_URL}/auth/reset-password`, data);
    return response.data;
  }

  async verifyEmail(token: string): Promise<AuthReponse> {
    const response = await axios.post<AuthReponse>(`${API_URL}/auth/verify-email`, { token });
    return response.data;
  }

  async resendVerificationEmail(email: string): Promise<AuthReponse> {
    const response = await axios.post<AuthReponse>(`${API_URL}/auth/resend-verification`, { email });
    return response.data;
  }
}

export const authApi = new AuthApi();
